import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Validate phone number (basic validation)
  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional
    // Remove the unnecessary escape character
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    return phoneRegex.test(phone) || phone.length >= 10;
  };

  // Validate email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate that at least email or phone is provided
    if (!email && !phone) {
      setError('Please provide either an email address or phone number');
      return;
    }

    // Validate email if provided
    if (email && !validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate phone if provided
    if (phone && !validatePhone(phone)) {
      setError('Please enter a valid phone number (e.g., +1234567890 or 1234567890)');
      return;
    }

    // Password validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your full name');
      return;
    }

    setLoading(true);
    
    const registrationData = {
      name: name.trim(),
      ...(email && { email: email.toLowerCase().trim() }),
      ...(phone && { phone: phone.trim() }),
      password
    };
    
    const result = await register(registrationData);
    
    if (result.success && result.user) {
      const firstName = result.user.name?.split(' ')[0] || 'User';

      sessionStorage.setItem('justRegistered', 'true');
      sessionStorage.setItem('userName', firstName);

      // Check whether registration was initiated from checkout
      const returnTo = location.state?.returnTo;
      const checkoutState = location.state?.checkoutState;

      if (returnTo === '/checkout' && checkoutState) {
        // Registration automatically authenticated the user through
        // AuthContext.register(), so they can now enter checkout.
        navigate('/checkout', {
          state: checkoutState,
          replace: true
        });
      } else {
        // Normal registration from the regular Create Account page
        navigate('/', {
          replace: true
        });
      }
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Create Account</h2>
        
        {error && <div className="auth-error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Full Name"
              autoComplete="name"
              className="auth-input"
            />
          </div>
          
          <div className="form-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Email Address"
              autoComplete="email"
              className="auth-input"
            />
          </div>
          
          <div className="form-group">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone Number"
              autoComplete="tel"
              className="auth-input"
            />
            <small className="input-hint">
              Recommended for login & delivery updates
            </small>
          </div>
          
          <div className="form-group">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Password"
              autoComplete="new-password"
              className="auth-input"
            />
            <small className="input-hint">
              Minimum of 6 characters
            </small>
          </div>
          
          <div className="form-group">
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm Password"
              autoComplete="new-password"
              className="auth-input"
            />
          </div>
          
          <button type="submit" disabled={loading} className="auth-button">
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>
        
        <p className="auth-link">
          Already have an account? <Link to="/login">Sign In</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;